// Client API per le chiamate al backend

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

// Definizione dei tipi per le risposte dell'API
interface ApiResponse<T> {
    data: T;
    message: string;
    success: boolean;
}

class ApiClient {
    private client: AxiosInstance;

    constructor(baseURL: string) {
        this.client = axios.create({
            baseURL,
            timeout: 300000, // 5 minuti per operazioni di sync
            headers: {
                'Content-Type': 'application/json',
            },
        });
        
        console.log('ApiClient initialized with baseURL:', baseURL);

        // Interceptor per le richieste
        this.client.interceptors.request.use(
            (config) => {
                // Aggiungi il token di autenticazione alle richieste se presente
                const token = localStorage.getItem('authToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Interceptor per le risposte
        this.client.interceptors.response.use(
            (response) => {
                return response;
            },
            (error) => {
                // Gestisci gli errori globalmente
                console.log('API Error intercepted:', {
                    status: error.response?.status,
                    url: error.config?.url,
                    method: error.config?.method,
                    error: error.message,
                    timestamp: new Date().toISOString()
                });
                
                if (error.response?.status === 401) {
                    console.warn('401 Unauthorized - removing token and redirecting to login');
                    localStorage.removeItem('authToken');
                    // Invece di forzare window.location.href, usa il router React
                    // Solo se non siamo già nella pagina di login
                    if (window.location.pathname !== '/login') {
                        console.log('Redirecting to login due to 401');
                        window.location.href = '/login';
                    }
                }
                return Promise.reject(error);
            }
        );
    }

    // Metodi HTTP generici
    async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
        return this.client.get<ApiResponse<T>>(url, config);
    }

    async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
        return this.client.post<ApiResponse<T>>(url, data, config);
    }

    async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
        return this.client.put<ApiResponse<T>>(url, data, config);
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<ApiResponse<T>>> {
        return this.client.delete<ApiResponse<T>>(url, config);
    }

    // Metodo specifico per operazioni di sync con timeout esteso
    async syncOperation<T>(url: string, data?: any, timeoutMs: number = 600000): Promise<AxiosResponse<ApiResponse<T>>> {
        const config: AxiosRequestConfig = {
            timeout: timeoutMs, // Timeout personalizzabile (default 10 minuti)
            headers: {
                'Content-Type': 'application/json',
            }
        };
        
        return this.client.post<ApiResponse<T>>(url, data, config);
    }

    // Metodi specifici per l'autenticazione
    async login(username: string, password: string): Promise<AxiosResponse<ApiResponse<{ token: string }>>> {
        return this.post<{ token: string }>('/auth/login', { username, password });
    }

    async register(username: string, email: string, password: string): Promise<AxiosResponse<ApiResponse<{ token: string }>>> {
        return this.post<{ token: string }>('/auth/register', { username, email, password });
    }

    // Metodi specifici per le unità eroganti
    async getExecutionUnits(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/execution-units');
    }

    async createExecutionUnit(data: any): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.post<any>('/execution-units', data);
    }

    async updateExecutionUnit(id: string, data: any): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.put<any>(`/execution-units/${id}`, data);
    }

    async deleteExecutionUnit(id: string): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.delete<any>(`/execution-units/${id}`);
    }

    // Metodi specifici per le agende
    async getDiaries(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diaries');
    }

    async createDiary(data: any): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.post<any>('/diaries', data);
    }

    async updateDiary(id: string, data: any): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.put<any>(`/diaries/${id}`, data);
    }

    async deleteDiary(id: string): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.delete<any>(`/diaries/${id}`);
    }

    // Metodi specifici per le prenotazioni
    async getBookings(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/bookings');
    }

    async createBooking(data: any): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.post<any>('/bookings', data);
    }

    async updateBooking(id: string, data: any): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.put<any>(`/bookings/${id}`, data);
    }

    async deleteBooking(id: string): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.delete<any>(`/bookings/${id}`);
    }

    // Metodi specifici per dati form agenda
    async getDiaryFormData(): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.get<any>('/diary-form-data/all');
    }

    async getExecutionUnitsForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/execution-units');
    }

    async getAuthoritiesForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/authorities');
    }

    async getHospitalsForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/hospitals');
    }

    async getSitesForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/sites');
    }

    async getSupplyModesForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/supply-modes');
    }

    async getPriorityClassesForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/priority-classes');
    }

    async getExamsForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/exams');
    }

    async getDoctorsForDiary(): Promise<AxiosResponse<ApiResponse<any[]>>> {
        return this.get<any[]>('/diary-form-data/doctors');
    }

    async syncAllDiaryData(): Promise<AxiosResponse<ApiResponse<any>>> {
        return this.post<any>('/diary-form-data/sync-all');
    }
}

// Crea e esporta un'istanza del client API
// Usa production URL se nessuna variabile d'ambiente è definita
const getApiBaseUrl = () => {
    // Prova prima le variabili Vite
    if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL;
    // Poi le variabili React (legacy)
    if (import.meta.env.REACT_APP_API_BASE_URL) return import.meta.env.REACT_APP_API_BASE_URL;
    // Default production URL
    return 'https://recupmanager.studiohard.it/api';
};

const apiClient = new ApiClient(getApiBaseUrl());

export default apiClient;
